/**
 * editVisit — PATCHes /api/projects/calllog/{calllogid}/ with the full
 * body of a PAST-visit edit made from the Edit Visit page (#1987).
 *
 * Unlike checkOut/patchOdometer (which each carry a narrow slice of a
 * CallLog), editVisit carries the ENTIRE edit form in one atomic PATCH:
 * times, team flags, notes, odometer, signature, AND the signed-visit
 * edit-audit fields (edited_after_signature / edit_timestamp /
 * edit_reason). A past visit is a server-sourced CallLog with no local
 * visit_task_sessions row, so the call site first adopts it via
 * adoptServerCallLog() and enqueues this op against the adopted uuid.
 *
 * Carrying every field in one op is the whole point: the prior dual-write
 * only queued odometer, silently dropping signature + edit-tracking — the
 * field-drop bug this op exists to kill.
 *
 * Reconcile is a no-op: the PATCH response is the rep's own fields echoed
 * back; the local row was already updated in applyLocal.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import { emit } from '../../utils/eventLog';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface EditVisitInput {
  visitLocalId: string;
  /** Check-in time. HH:MM or ISO; the server PATCH accepts both. */
  stcalltime?: string;
  /** Check-out time. HH:MM or ISO. */
  edcalltime?: string;
  teamproject?: boolean;
  teamleader?: boolean;
  /** Visit notes. Pass through verbatim (may be empty string to clear). */
  notes?: string;
  driveodostart?: number;
  driveodoend?: number;
  /**
   * Drive-to times. The editor corrects these — it is the only place a rep can
   * fix an inflated drive (#3019) — and this op used to have nowhere to put
   * them, so routing the editor through the queue without them would have
   * dropped exactly the field the correction was for (#3529).
   */
  stdrivetime?: string;
  eddrivetime?: string;
  /** Manager signature (data URL) + name, mirroring checkOut's wire names. */
  signature?: string | null;
  manager_name?: string;
  no_signature_reason?: string | null;
  /** Signed-visit edit-audit fields — no other op carries these. */
  edited_after_signature?: boolean;
  edit_timestamp?: string;
  edit_reason?: string;
}

export interface EditVisitResponse {
  calllogid: number;
}

export const editVisitOp: WriteOp<EditVisitInput, EditVisitInput, EditVisitResponse> = {
  kind: 'editVisit',

  async applyLocal(input) {
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, {
      ...match,
      startTime: input.stcalltime ?? match.startTime,
      endTime: input.edcalltime ?? match.endTime,
      checkOutTime: input.edcalltime ?? match.checkOutTime,
      teamproject: input.teamproject ?? match.teamproject,
      teamleader: input.teamleader ?? match.teamleader,
      notes: input.notes ?? match.notes,
      startOdometer: input.driveodostart ?? match.startOdometer,
      endOdometer: input.driveodoend ?? match.endOdometer,
      managerName: input.manager_name ?? match.managerName,
      signatureDataUrl: input.signature ?? match.signatureDataUrl,
      noSignatureReason: input.no_signature_reason ?? match.noSignatureReason,
    });

    // Event stream (#1950 P2): a time-record change → time_edited, so the
    // synced/total "Time edits" count is exact. Best-effort.
    //
    // The event carries WHAT CHANGED, not merely that something did. A payload
    // of {action:'edit'} left the Manager board and reconstruct_active_day
    // replaying the original times forever: they read the ledger, so an edit
    // they cannot see is an edit that never happened to them. `match` holds the
    // pre-edit values, so both sides of each change are recorded here — the one
    // place that has them.
    const changed: Record<string, { from: unknown; to: unknown }> = {};
    const track = (field: string, to: unknown, from: unknown) => {
      if (to === undefined) return;
      changed[field] = { from: from ?? null, to };
    };
    track('stcalltime', input.stcalltime, match.startTime);
    track('edcalltime', input.edcalltime, match.endTime ?? match.checkOutTime);
    // The session row keeps no drive times, so the prior value is genuinely
    // unknown here rather than null-because-empty. Recorded as unknown instead
    // of guessing one — the new value is the part a reader needs either way.
    track('stdrivetime', input.stdrivetime, 'unknown');
    track('eddrivetime', input.eddrivetime, 'unknown');

    try {
      await emit({
        event_type: 'time_edited',
        store_id: (match.storeId as number) ?? null,
        project_id: (match.projectId as number) ?? null,
        calllog_id: (match.callLogId as number) ?? null,
        payload: {
          local_id: input.visitLocalId,
          action: 'edit',
          changed,
          edited_after_signature: input.edited_after_signature ?? false,
          edit_reason: input.edit_reason ?? null,
        },
      });
    } catch {
      /* event emit is best-effort */
    }
  },

  serialize(input, localId) {
    return {
      action: 'editVisit',
      endpoint: '/api/projects/calllog/{calllogid}/',
      method: 'PATCH',
      payload: input,
      priority: 30,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as EditVisitInput;
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
    const url = urlOrShortCircuit;

    // Forward every supplied field — the full edit body. Only visitLocalId
    // (the queue routing key) is stripped; everything else is a CallLog field.
    const body: Record<string, unknown> = {};
    if (input.stcalltime !== undefined) body.stcalltime = input.stcalltime;
    if (input.edcalltime !== undefined) body.edcalltime = input.edcalltime;
    if (input.teamproject !== undefined) body.teamproject = input.teamproject;
    if (input.teamleader !== undefined) body.teamleader = input.teamleader;
    if (input.notes !== undefined) body.notes = input.notes;
    if (input.driveodostart !== undefined) body.driveodostart = input.driveodostart;
    if (input.driveodoend !== undefined) body.driveodoend = input.driveodoend;
    if (input.stdrivetime !== undefined) body.stdrivetime = input.stdrivetime;
    if (input.eddrivetime !== undefined) body.eddrivetime = input.eddrivetime;
    if (input.signature !== undefined) body.signature = input.signature;
    if (input.manager_name !== undefined) body.manager_name = input.manager_name;
    if (input.no_signature_reason !== undefined) body.no_signature_reason = input.no_signature_reason;
    if (input.edited_after_signature !== undefined)
      body.edited_after_signature = input.edited_after_signature;
    if (input.edit_timestamp !== undefined) body.edit_timestamp = input.edit_timestamp;
    if (input.edit_reason !== undefined) body.edit_reason = input.edit_reason;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json().catch(() => ({}));
    const callLog = data.data || data;
    return {
      status: 'success',
      serverId: typeof callLog?.calllogid === 'number' ? callLog.calllogid : undefined,
      retriable: false,
    };
  },

  async reconcile() {
    // Server echoes the rep's own fields; local row already updated in applyLocal.
  },

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type EditVisitOp = typeof editVisitOp;
